"use client";

import { useAuth } from "@/components/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { trackOAuthSignupConversion } from "@/lib/analytics/google-ads-tracking";
import { getPostHogDistinctIdForUser, getRevenueCatAppUserId } from "@/lib/analytics/identity";
import {
  consumeOAuthPostHogBootstrap,
  stripOAuthPostHogBootstrapParams,
  stripOAuthSignupMarker,
} from "@/lib/analytics/oauth-posthog-bootstrap";
import {
  filterPostHogExceptionEvent,
  sanitizePostHogUrlString,
} from "@/lib/analytics/posthog-exception-filter";
import {
  buildEventAttributionProperties,
  buildInitialAttributionProperties,
  extractUTMParams,
  getAttributionData,
  hasAttributionSignal,
  saveAttributionData,
} from "@/lib/analytics/utm-tracker";
import { getGuestSpatiusAvatarExposureProperties } from "@/lib/experiments/guestSpatiusAvatar";
import { resetGuestVoiceIdentity } from "@/lib/experiments/guestVoiceRuntimeClient";
import { getWebappSignupBeforeDemoAttributionProperties } from "@/lib/experiments/webappSignupBeforeDemo";
import { syncWebContact } from "@/lib/lifecycle/sync-web-contact";
import { createLogger } from "@/lib/logger";
import { logPrerenderBreadcrumb } from "@/lib/prerender-diagnostics";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import "posthog-js/dist/web-vitals";
import { usePostHog } from "posthog-js/react";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import React, { Suspense, useEffect, useRef, useState } from "react";

const logger = createLogger("providers");

const ATTRIBUTION_CAPTURE_KEY = "chicky_attribution_capture_v1";
const LAST_IDENTIFIED_USER_KEY = "chicky_last_identified_user";
const OAUTH_SIGNUP_CAPTURE_KEY = "chicky_oauth_signup_capture_v1";

const isBenignPostHogSessionRecordingError = (error: unknown) =>
  error instanceof Error &&
  error.message.includes("Called on script loaded before session recording is available");

logPrerenderBreadcrumb("app/providers module evaluated", {
  hasWindow: typeof window !== "undefined",
  hasDocument: typeof document !== "undefined",
});

// Component to capture UTM parameters and attribution
function UTMCapture() {
  const searchParams = useSearchParams();
  const posthogClient = usePostHog();

  useEffect(() => {
    // Guard against SSR/prerendering
    if (typeof window === "undefined" || typeof document === "undefined" || !searchParams) return;

    const previousAttribution = getAttributionData();

    // Extract UTM parameters from URL
    const utmParams = extractUTMParams(searchParams, window.location.search);
    const analyticsUtmParams = { ...utmParams };
    delete analyticsUtmParams.oppref;

    // Save attribution data (first-touch model)
    const attributionData = saveAttributionData(
      utmParams,
      typeof document !== "undefined" ? document.referrer : "",
      window.location.href
    );

    // Set PostHog super properties for all future events
    if (posthogClient && attributionData) {
      posthogClient.register(buildInitialAttributionProperties(attributionData));

      // Capture once per first-touch source, including explicit UTMs/click IDs and inferred referrers.
      const capturedFirstTouchThisRun =
        !hasAttributionSignal(previousAttribution) && hasAttributionSignal(attributionData);
      if (capturedFirstTouchThisRun || Object.keys(utmParams).length > 0) {
        try {
          const fingerprintPayload = {
            utmParams: analyticsUtmParams,
            has_openai_ads_oppref: !!utmParams.oppref,
            referrer: attributionData.referrer,
            landing_page: attributionData.landing_page,
            first_touch_source: attributionData.utm_source,
            gclid: attributionData.gclid,
            fbclid: attributionData.fbclid,
          };
          const fingerprint = JSON.stringify(fingerprintPayload);
          const now = Date.now();
          const dedupeWindowMs = 1000 * 60 * 60 * 6; // 6 hours

          const stored = sessionStorage.getItem(ATTRIBUTION_CAPTURE_KEY);
          let shouldCapture = true;

          if (stored) {
            try {
              const parsed = JSON.parse(stored) as { fingerprint: string; timestamp: number };
              if (
                parsed.fingerprint === fingerprint &&
                now - (parsed.timestamp || 0) < dedupeWindowMs
              ) {
                shouldCapture = false;
              }
            } catch (error) {
              logger.warn("Failed to parse stored attribution capture key", error);
            }
          }

          if (shouldCapture) {
            posthogClient.capture("campaign_attribution_captured", {
              ...analyticsUtmParams,
              ...buildEventAttributionProperties(attributionData),
              attribution_capture_trigger:
                Object.keys(utmParams).length > 0 ? "url_params" : "referrer_inference",
              has_click_id: !!(
                attributionData.gclid ||
                attributionData.fbclid ||
                attributionData.latest_paid_gclid ||
                attributionData.latest_paid_gbraid ||
                attributionData.latest_paid_wbraid ||
                attributionData.latest_paid_dclid
              ),
              has_openai_ads_oppref: !!utmParams.oppref,
              has_utm: !!(
                attributionData.utm_source ||
                attributionData.utm_medium ||
                attributionData.utm_campaign
              ),
            });

            sessionStorage.setItem(
              ATTRIBUTION_CAPTURE_KEY,
              JSON.stringify({ fingerprint, timestamp: now })
            );
          }
        } catch (error) {
          logger.warn("Failed to capture campaign attribution with dedupe", error);
        }
      }
    }
  }, [searchParams, posthogClient]);

  return null;
}

// Wrap UTMCapture in Suspense
function SuspendedUTMCapture() {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return null;
  return (
    <Suspense fallback={null}>
      <UTMCapture />
    </Suspense>
  );
}

// This is the main provider component
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [isPostHogReady, setIsPostHogReady] = useState(false);

  logPrerenderBreadcrumb("PostHogProvider render start", {
    hasWindow: typeof window !== "undefined",
    hasDocument: typeof document !== "undefined",
  });

  // Initialize PostHog on mount
  useEffect(() => {
    // Check if we already have an active PostHog session to prevent duplicates
    if (typeof window !== "undefined") {
      const hasActiveSession = window.localStorage.getItem("posthog_session_active");
      if (hasActiveSession === "true" && posthog.__loaded) {
        logger.warn("PostHog session already active, skipping duplicate initialization");
        setIsPostHogReady(true);
        return;
      }
      // Mark session as active
      window.localStorage.setItem("posthog_session_active", "true");

      // Add global error handler to suppress PostHog cross-origin SecurityError
      // This error occurs when PostHog session recording tries to access
      // window.removeEventListener on cross-origin iframes during stop()
      const originalOnError = window.onerror;
      window.onerror = (message, source, lineno, colno, error) => {
        if (
          error instanceof DOMException &&
          error.name === "SecurityError" &&
          (source?.includes("posthog") || source?.includes("lazy-loaded-session-recorder"))
        ) {
          // Silently suppress PostHog cross-origin errors
          return true;
        }
        // Call original handler if it exists
        if (originalOnError) {
          return originalOnError(message, source, lineno, colno, error);
        }
        return false;
      };
    }
    // Ensure this runs only on the client
    if (typeof window !== "undefined") {
      const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
      const posthogUiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || "https://eu.posthog.com";

      if (posthogKey) {
        // Check if PostHog is already initialized to prevent duplicates
        if (!posthog.__loaded) {
          // Only accept OAuth IDs staged in this tab immediately before redirect.
          const urlParams = new URLSearchParams(window.location.search);
          const oauthBootstrap = consumeOAuthPostHogBootstrap(window.sessionStorage, urlParams);

          const initOptions: any = {
            api_host: posthogHost,
            ui_host: posthogUiHost,
            // person_profiles: 'identified_only', // Consider this for GDPR, default is 'always'
            capture_pageview: false, // We'll capture pageviews manually with SuspendedPostHogPageView
            // The SDK's legacy default only captures pageleave when automatic pageviews are enabled.
            capture_pageleave: true,
            session_recording: {
              // Prevent duplicate session recordings and cross-origin security errors
              recordCrossOriginIframes: false,
              // Minimize chance of duplicate recordings by configuring masking
              maskAllInputs: false,
            },
            // Disable automatic session recording if it's causing issues
            disable_session_recording: false,
            // Advanced settings to prevent duplicates
            advanced_disable_decide: false,
            persistence: "localStorage+cookie",
            // Ensure only one instance per domain
            cross_subdomain_cookie: false,
            before_send: filterPostHogExceptionEvent,
            loaded: () => setIsPostHogReady(true),
          };

          // Bootstrap once with the exact pair staged before the OAuth redirect.
          if (oauthBootstrap) {
            initOptions.bootstrap = {
              sessionID: oauthBootstrap.sessionId,
              distinctID: oauthBootstrap.distinctId,
            };
            logger.info(
              `PostHog bootstrapped with session: ${oauthBootstrap.sessionId.substring(0, 8)}...`
            );
          }

          posthog.init(posthogKey, initOptions);
          if (posthog.__loaded) setIsPostHogReady(true);
          logger.info("PostHog initialized successfully");
        } else {
          setIsPostHogReady(true);
          logger.info("PostHog already initialized, skipping duplicate initialization");
        }
      } else {
        logger.info("PostHog API key or host not found. PostHog will not be initialized.");
      }

      const cleanOAuthUrl = stripOAuthPostHogBootstrapParams(window.location.href);
      if (cleanOAuthUrl) {
        window.history.replaceState(window.history.state, "", cleanOAuthUrl);
      }
    }

    // Cleanup function to handle hot reloads in development mode
    return () => {
      if (typeof window !== "undefined") {
        // Clear the session active flag
        try {
          window.localStorage.removeItem("posthog_session_active");
        } catch {
          // Ignore storage access errors
        }

        if (process.env.NODE_ENV === "development") {
          // In development mode, stop any active session recordings to prevent duplicates
          // during hot reloads. Wrap in try-catch to handle cross-origin security errors
          // that can occur when PostHog tries to access window properties in iframes.
          if (posthog.__loaded && posthog.sessionRecording) {
            try {
              posthog.sessionRecording.stopRecording();
              logger.info(
                "Stopped PostHog session recording due to component unmount (hot reload)"
              );
            } catch (error) {
              // SecurityError can occur when PostHog tries to access cross-origin iframes
              // during cleanup. This is safe to ignore as the session will be cleaned up
              // when the page unloads.
              if (error instanceof DOMException && error.name === "SecurityError") {
                logger.info("PostHog cleanup skipped due to cross-origin security policy");
              } else if (isBenignPostHogSessionRecordingError(error)) {
                logger.info("PostHog cleanup skipped until session recorder finishes loading");
              } else {
                logger.warn("Error stopping PostHog session recording:", error);
              }
            }
          }
        }
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount

  // Wrap children with PostHog's own provider
  return (
    <PHProvider client={posthog}>
      <SuspendedUTMCapture /> {/* Component to capture UTM parameters */}
      <SuspendedPostHogIdentifyUser isPostHogReady={isPostHogReady} />{" "}
      {/* Component to handle user identification */}
      <SuspendedPostHogPageView /> {/* Component to handle page views */}
      <AuthModal />
      {children}
    </PHProvider>
  );
}

// Component to handle user identification
function PostHogIdentifyUser({ isPostHogReady }: { isPostHogReady: boolean }) {
  const { user } = useAuth();
  const posthogClient = usePostHog();
  const lastIdentifiedDistinctIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (user && posthogClient && isPostHogReady) {
      // Get attribution data to attach to user profile
      const attributionData = getAttributionData();
      const distinctId = getPostHogDistinctIdForUser(user);
      const revenueCatAppUserId = getRevenueCatAppUserId(user);

      if (
        lastIdentifiedDistinctIdRef.current &&
        lastIdentifiedDistinctIdRef.current !== distinctId
      ) {
        void resetGuestVoiceIdentity(posthogClient);
      }

      // Keep PostHog on the opaque Supabase UUID. RevenueCat's email appUserId
      // stays as a property and is resolved back to this ID in webhook events.
      posthogClient.identify(distinctId, {
        email: user.email,
        auth_user_id: user.id,
        user_id: user.id,
        revenuecat_app_user_id: revenueCatAppUserId || undefined,
        created_at: user.created_at,
        last_sign_in_at: user.last_sign_in_at,
        auth_provider: user.app_metadata?.provider,
        ...buildInitialAttributionProperties(attributionData),
      });

      // Track user identification event with attribution
      let shouldCaptureIdentify = true;
      try {
        const stored = localStorage.getItem(LAST_IDENTIFIED_USER_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as { userId: string; timestamp: number };
          const now = Date.now();
          const dedupeWindowMs = 1000 * 60 * 60 * 6; // 6 hours
          if (parsed.userId === distinctId && now - (parsed.timestamp || 0) < dedupeWindowMs) {
            shouldCaptureIdentify = false;
          }
        }
      } catch (error) {
        logger.warn("Failed to parse last identified user cache", error);
      }

      if (shouldCaptureIdentify) {
        posthogClient.capture("user_identified", {
          auth_user_id: user.id,
          user_id: user.id,
          revenuecat_app_user_id: revenueCatAppUserId || undefined,
          analytics_identity: "supabase_user_id",
          created_at: user.created_at,
          has_attribution: hasAttributionSignal(attributionData),
          ...buildEventAttributionProperties(attributionData),
        });

        try {
          localStorage.setItem(
            LAST_IDENTIFIED_USER_KEY,
            JSON.stringify({ userId: distinctId, timestamp: Date.now() })
          );
        } catch (error) {
          logger.warn("Failed to persist last identified user cache", error);
        }
      }

      void syncWebContact(user.id);

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("oauth_signup") === "true") {
        const signupFingerprint = `${distinctId}:google_oauth`;
        let shouldCaptureSignup = true;
        try {
          shouldCaptureSignup =
            sessionStorage.getItem(OAUTH_SIGNUP_CAPTURE_KEY) !== signupFingerprint;
        } catch (error) {
          logger.warn("Failed to read OAuth signup capture cache", error);
        }

        let signupQueued = !shouldCaptureSignup;
        if (shouldCaptureSignup) {
          try {
            posthogClient.capture("user_signed_up", {
              method: "google_oauth",
              signup_source: "oauth_callback",
              auth_user_id: user.id,
              revenuecat_app_user_id: revenueCatAppUserId || undefined,
              ...buildEventAttributionProperties(attributionData),
              ...getGuestSpatiusAvatarExposureProperties(),
              ...getWebappSignupBeforeDemoAttributionProperties(),
            });
            trackOAuthSignupConversion({
              value: 0.1,
              currency: "USD",
            });
            try {
              sessionStorage.setItem(OAUTH_SIGNUP_CAPTURE_KEY, signupFingerprint);
            } catch (error) {
              logger.warn("Failed to persist OAuth signup capture cache", error);
            }
            signupQueued = true;
          } catch (error) {
            logger.warn("Failed to queue OAuth signup conversions", error);
          }
        }

        if (signupQueued) {
          const cleanOAuthUrl = stripOAuthSignupMarker(window.location.href);
          if (cleanOAuthUrl) {
            window.history.replaceState(window.history.state, "", cleanOAuthUrl);
          }
        }
      }

      lastIdentifiedDistinctIdRef.current = distinctId;

      // Sync attribution to RevenueCat for subscription tracking
      if (attributionData && revenueCatAppUserId) {
        import("@/lib/analytics/revenuecat-attribution")
          .then(({ syncAttributionToRevenueCat }) => {
            syncAttributionToRevenueCat(revenueCatAppUserId).catch((error) => {
              // Error is already logged in the sync function
              console.warn("Failed to trigger RevenueCat attribution sync:", error);
            });
          })
          .catch((error) => {
            console.warn("Failed to import RevenueCat attribution sync:", error);
          });
      }
    } else if (!user && posthogClient && lastIdentifiedDistinctIdRef.current) {
      // Only reset on a real transition from identified -> logged out.
      // Avoid resetting during OAuth callback hydration where user can be briefly null.
      posthogClient.capture("user_logged_out", {
        previous_distinct_id: lastIdentifiedDistinctIdRef.current,
      });
      void resetGuestVoiceIdentity(posthogClient);
      posthogClient.reset();
      lastIdentifiedDistinctIdRef.current = null;
      try {
        localStorage.removeItem(LAST_IDENTIFIED_USER_KEY);
        sessionStorage.removeItem(ATTRIBUTION_CAPTURE_KEY);
        sessionStorage.removeItem(OAUTH_SIGNUP_CAPTURE_KEY);
      } catch (error) {
        logger.warn("Failed to clear PostHog session caches", error);
      }
    }
  }, [user, posthogClient, isPostHogReady]);

  return null;
}

// Wrap PostHogIdentifyUser in Suspense
function SuspendedPostHogIdentifyUser({ isPostHogReady }: { isPostHogReady: boolean }) {
  // Only render the identifier if PostHog is likely initialized (key exists)
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return null;
  return (
    <Suspense fallback={null}>
      <PostHogIdentifyUser isPostHogReady={isPostHogReady} />
    </Suspense>
  );
}

// Component to handle page views
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogClient = usePostHog();

  useEffect(() => {
    // Guard against SSR/prerendering
    if (typeof window === "undefined" || !pathname || !searchParams || !posthogClient) return;

    if (pathname && searchParams && posthogClient) {
      const searchParamsString = searchParams.toString();
      let url = window.origin + pathname;
      if (searchParamsString) {
        url = url + "?" + searchParamsString;
      }
      posthogClient.capture("$pageview", { $current_url: sanitizePostHogUrlString(url) });
    }
  }, [pathname, searchParams.toString(), posthogClient]);

  return null;
}

// Wrap PostHogPageView in Suspense
function SuspendedPostHogPageView() {
  // Only render the pageview tracker if PostHog is likely initialized (key exists)
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return null;
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
